import { ArrowBack } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  PaginationItem,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_TableOptions,
  useMaterialReactTable,
} from 'material-react-table';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import DownloadData from 'packages/novous_web/src/common/components/DownloadData';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import downloadWhiteIcon from 'packages/novous_web/src/assets/icons/download.svg';
import addAccount from 'packages/novous_web/src/assets/icons/PlusWhiteStroke.svg';
import InputField from 'packages/novous_web/src/common/components/InputField';
import searchIcon from 'packages/novous_web/src/assets/icons/Icon.svg';
import editIcon from 'packages/novous_web/src/assets/icons/edit.svg';
import { MRT_Cell } from 'material-react-table';
import menu from 'packages/novous_web/src/assets/icons/menu.svg';
import deactivateIcon from 'packages/novous_web/src/assets/icons/deactivate-account.svg';
import activateIcon from 'packages/novous_web/src/assets/icons/activate-account.svg';
import deleteIcon from 'packages/novous_web/src/assets/icons/Delete.svg';
import CreateUserAccount from './components/CreateUserAccount';
import ActivateDeactivateAccount from './components/ActivateDeactivateAccount';
import useApiError from 'packages/novous_web/src/api/useApiError';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import DeleteAccount from './components/DeleteAccount';
import EditUserAccount from './components/EditUserAccount';

interface ScheduledReport {
  firstName: string;
  username: string;
  password: string;
  authorities: string;
  phone: string;
  address: {
    fullAddress: string;
    country: string;
  };
  status: number;
}

const data: ScheduledReport[] = [];

type ColumnObjType = {
  [key: string | number]: {
    name: string;
    status: boolean;
  };
};

export interface UserAccountForm {
  firstName: string;
  userName: string;
  authorities: string;
  email: string;
  phone: string;
  address: {
    fullAddress: string;
    country: string;
  };
  loginUserId?: string;
  employeeId?: string;
  password: string;
  confirmPassword: string;
}

const UserAccount = () => {
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertType, setAlertType] = useState<'error' | 'success' | 'warning' | 'info'>('error');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const navigate = useNavigate();
  const [selectedFormat, setSelectedFormat] = useState<string>('pdf');
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isCreateAccountOpen, setIsCreateAccountOpen] = useState(false);
  const location = useLocation();
  const vehicleData = location.state || {};
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const isSmallScreen = useMediaQuery('(max-width:850px)');
  const itemsPerPage = 10;
  const [selectAccount, setSelectedAccount] = useState<ScheduledReport | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pauseOpen, setPauseOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const [columnObj, setColumnObj] = useState<ColumnObjType>({
    firstName: { name: 'Name', status: true },
    username: { name: 'Username', status: true },
    password: { name: 'Password', status: true },
    authorities: { name: 'Role', status: true },
    phone: { name: 'Contact No.', status: true },
    actions: { name: 'Actions', status: true },
    address: { name: 'Address', status: true },
    status: { name: 'Status', status: true },
  });

  interface ScheduledReportResponse {
    data: {
      userList: ScheduledReport[];
    };
  }

  const {
    data: ScheduledReportData,
    error: ScheduledReportError,
    loading: ScheduledReportLoading,
    fetchData: ScheduledReportRequestData,
  } = useAxios<ScheduledReportResponse>(API_ENDPOINTS.users.USER_LIST_DATA, 'POST');

  useEffect(() => {
    ScheduledReportRequestData({
      userId: user.id,
    });
  }, []);

  const processedData = useMemo(() => {
    if (!ScheduledReportData?.data) return [];
    return (ScheduledReportData as ScheduledReportResponse)?.data?.userList?.map((report) => ({
      ...report,
      totalEventsCount: report.status ?? 0,
    }));
  }, [ScheduledReportData]);

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery) return processedData;

    return processedData.filter((report) => {
      return (
        report.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.authorities?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.address?.fullAddress?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.address?.country?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (report.status === 1 ? 'active' : 'inactive').includes(searchQuery.toLowerCase())
      );
    });
  }, [processedData, searchQuery]);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Calculate paginated data from filtered data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage]);

  // Calculate total pages based on filtered data
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const {
    data: downloadManagerReportData,
    error: downloadManagerReportError,
    loading: downloadManagerReportLoading,
    fetchData: fetchDownloadManagerReportData,
  } = useAxios<any>(`${API_ENDPOINTS.users.USER_DOWNLOAD}`, 'POST', 'blob');

  const handleDownload = async () => {
    fetchDownloadManagerReportData({
      fleetId: user?.id,
      fileType: selectedFormat,
    });
  };

  const handleFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFormat(event.target.value);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAccount(null);
  };

  const handelPauseReport = (report: ScheduledReport) => {
    setSelectedAccount(report);
    setPauseOpen(!pauseOpen);
  };

  const handleDeleteReport = (report: ScheduledReport) => {
    setSelectedAccount(report);
    setDeleteOpen(true);
  };

  const {
    data: createUsersAccountSave,
    error: createUsersAccountSaveError,
    loading: createUsersAccountSaveLoading,
    fetchData: fetchCreateUsersAccountSaveData,
  } = useApiError<any>(`${API_ENDPOINTS.users.CREATE_USER}`, 'POST');

  const handelCreateAccount = async (formData: UserAccountForm) => {
    setIsSendingEmail(true);
    const requestedData = {
      firstName: formData.firstName,
      userName: formData.userName,
      password: formData.password,
      authorities: formData.authorities,
      address: {
        fullAddress: formData.address.fullAddress,
        country: formData.address.country,
      },
      loginUserId: user.id,
      // employeeId: string,
      phone: formData.phone,
      email: formData.email,
    };
    try {
      const response = await fetchCreateUsersAccountSaveData(requestedData);

      if (response.status === 201) {
        setAlertMessage(response?.message);
        setAlertType('success');
        setAlertOpen(true);
        setIsCreateAccountOpen(false);
      } else {
        setAlertMessage(response?.error);
        setAlertType('error');
        setAlertOpen(true);
        setIsCreateAccountOpen(false);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setAlertMessage((error as Error).message);
      setAlertType('error');
      setAlertOpen(true);
    } finally {
      setIsSendingEmail(false);
    }
  };

  useEffect(() => {
    if (downloadManagerReportData) {
      try {
        if (downloadManagerReportData instanceof Blob) {
          const url = window.URL.createObjectURL(downloadManagerReportData);
          const a = document.createElement('a');
          a.href = url;
          let extension = selectedFormat.toLowerCase();
          if (extension === 'excel') {
            extension = 'xlsx'; // Use xlsx for Excel format
          }
          const fileName = `user_account_data.${extension}`;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          console.error('Invalid response format. Expected a Blob.');
        }
      } catch (error) {
        console.error('Error downloading file:', error);
      } finally {
        setIsDownloadModalOpen(false);
      }
    }
  }, [downloadManagerReportData]);

  const handleDownloadModalClose = () => {
    setIsDownloadModalOpen(false);
  };

  const handleCreateAccountModalClose = () => {
    setIsCreateAccountOpen(false);
  };
  const allColumns: MRT_ColumnDef<ScheduledReport>[] = useMemo(
    () => [
      {
        accessorKey: 'firstName',
        header: 'Name',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 150,
        muiTableBodyCellProps: { sx: { padding: '8px' } },
        Cell: ({ cell }) => {
          const reportName = cell.getValue<string>();
          return (
            <Typography variant="body2" fontWeight={400} noWrap>
              {reportName ? reportName : 'NA'}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'username',
        header: 'Username',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 150,
        muiTableBodyCellProps: { sx: { padding: '8px' } },
        Cell: ({ cell }) => {
          const reportName = cell.getValue<string>();
          return (
            <Typography variant="body2" fontWeight={400} noWrap>
              {reportName ? reportName : 'NA'}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'password',
        header: 'Password',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 150,
        muiTableBodyCellProps: { sx: { padding: '8px' } },
        Cell: ({ cell }) => {
          const reportName = cell.getValue<string>();
          return (
            <Typography variant="body2" fontWeight={400} noWrap>
              {reportName ? reportName : 'NA'}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'authorities',
        header: 'Role',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 150,
        muiTableBodyCellProps: { sx: { padding: '8px' } },
        Cell: ({ cell }) => {
          const reportName = cell.getValue<string>();
          return (
            <Typography variant="body2" fontWeight={400} noWrap>
              {reportName ? reportName : 'NA'}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'phone',
        header: 'Contact No.',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 150,
        muiTableBodyCellProps: { sx: { padding: '8px' } },
        Cell: ({ cell }) => {
          const reportName = cell.getValue<string>();
          return (
            <Typography variant="body2" fontWeight={400} noWrap>
              {reportName ? reportName : 'NA'}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'address',
        header: 'Address',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 150,
        muiTableBodyCellProps: { sx: { padding: '8px' } },
        Cell: ({ cell }) => {
          const address = cell.getValue<{ fullAddress: string; country: string }>();
          return (
            <Typography variant="body2" fontWeight={400} noWrap>
              {address?.fullAddress || address?.country ? `${address.fullAddress}, ${address.country}` : 'NA'}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 150,
        muiTableBodyCellProps: { sx: { padding: '8px' } },
        Cell: ({ cell }) => {
          const reportStatus = cell.getValue<number>();
          const statusText = reportStatus === 1 ? 'Active' : reportStatus === 0 ? 'Inactive' : 'NA';

          return (
            <Typography
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '12px',
                lineHeight: '18px',
                height: '24px',
                borderRadius: '15px',
                color: reportStatus === 0 ? '#C83333' : '#219653',
                backgroundColor: reportStatus === 0 ? '#F7E1E1' : '#D9EDE6',
                fontWeight: reportStatus === 0 ? 400 : 400,
                width: reportStatus === 1 ? '50px' : '62px',
                textAlign: 'center',
              }}
            >
              {reportStatus === 1 ? 'Active' : reportStatus === 0 ? 'Inactive' : 'NA'}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 140,
        muiTableBodyCellProps: {
          sx: { maxWidth: '140px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ row }: { row: MRT_Cell<ScheduledReport>['row'] }) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAccount(row.original);
                setIsEditModalOpen(true);
              }}
            >
              <img src={editIcon} alt="EditIcon" width={16} height={16} />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAccount(row.original);
                setAnchorEl(e.currentTarget);
              }}
            >
              <img
                src={menu}
                alt="Actions"
                width={4}
                height={16}
                style={{ marginLeft: 16, cursor: 'pointer' }}
              />
            </IconButton>
          </div>
        ),
      },
    ],
    [isSmallScreen],
  );

  const columns: MRT_ColumnDef<ScheduledReport>[] = useMemo(() => {
    return allColumns.filter((column) => {
      const key = column.accessorKey as keyof ColumnObjType;
      return columnObj[key]?.status;
    });
  }, [allColumns, columnObj]);

  // Table options
  const tableOptions: MRT_TableOptions<ScheduledReport> = {
    columns,
    data: paginatedData,
    enablePagination: true,
    enableColumnFilters: false,
    enableSorting: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableFullScreenToggle: false,
    enableColumnResizing: false,
    enableToolbarInternalActions: false,
    initialState: {
      columnPinning: {
        left: ['deviceId', 'date_time', 'vehicle'], // Fix these columns to the left
        right: [], // No pinned columns on the right
      },
    },
    muiTableBodyProps: {
      sx: {
        padding: 0,
        margin: 0,
      },
    },
  };

  // Initialize the table
  const table = useMaterialReactTable(tableOptions);

  return (
    <>
      <AlertToast
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
      <div className="flex flex-col gap-4 w-full">
        <div className="w-full">
          <button className="text-blue-600 font-medium flex items-center" onClick={() => navigate(-1)}>
            <ArrowBack className="mr-1" /> Back
          </button>
        </div>
        <div className=" w-full flex lg:flex-row md:flex-row flex-col justify-between gap-4 xs:flex-col items-start">
          <div className="flex justify-center items-center gap-4 relative">
            <img src={searchIcon} alt="search-icon" className="absolute left-4 top-[45%] z-10" />
            <InputField
              name="finalColumnsSearch"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search Recently Generated"
              type="text"
              extraClass="pl-10"
            />
          </div>
          <div className="flex gap-4">
            <CustomIconButton
              icon={<img src={downloadWhiteIcon} alt="download" width={16} height={16} />}
              text="Download"
              background="transparent"
              variant="outlined"
              onClick={() => setIsDownloadModalOpen(!isDownloadModalOpen)}
            />
            <CustomIconButton
              icon={<img src={addAccount} alt="download" width={16} height={16} />}
              text="Add Account"
              // background="transparent"
              variant="contained"
              onClick={() => setIsCreateAccountOpen(!isCreateAccountOpen)}
            />
          </div>
        </div>

        <MaterialReactTable table={table} />

        <DownloadData
          isDownloadModalOpen={isDownloadModalOpen}
          handleDownloadModalClose={handleDownloadModalClose}
          selectedFormat={selectedFormat}
          handleFormatChange={handleFormatChange}
          handleDownload={handleDownload}
          modalTitle="Download User Account Data"
          modalDescription="Please select a file format for your download."
          formatLabel="Format"
          formatExcelLabel="Excel"
          formatCsvLabel="CSV"
          formatPdfLabel="PDF"
          cancelButtonText="Cancel"
          downloadButtonText="Download"
        />

        <CreateUserAccount
          isCreateAccountOpen={isCreateAccountOpen}
          handleCreateAccountModalClose={handleCreateAccountModalClose}
          onSubmit={handelCreateAccount}
          modalTitle="Add User Account"
          modalDescription="Enter the details below to add  new user account."
          cancelButtonText="Cancel"
          submitButtonText={isSendingEmail ? 'Sending...' : 'Submit'}
        />

        <EditUserAccount
          isEditModalOpen={isEditModalOpen}
          handleEditModalClose={() => setIsEditModalOpen(false)}
          selectAccount={selectAccount}
          modalTitle="Edit User Account"
          modalDescription="Enter the details below to edit user account."
          cancelButtonText="Cancel"
          submitButtonText={isSendingEmail ? 'Updating...' : 'Update'}
        />

        {pauseOpen && (
          <ActivateDeactivateAccount
            pauseOpen={pauseOpen}
            setPauseOpen={setPauseOpen}
            selectAccount={selectAccount}
            onDeleteSuccess={() => {
              ScheduledReportRequestData({
                userId: user.id,
              });
            }}
          />
        )}

        {deleteOpen && (
          <DeleteAccount
            deleteOpen={deleteOpen}
            setDeleteOpen={setDeleteOpen}
            selectAccount={selectAccount}
            onDeleteSuccess={() => {
              ScheduledReportRequestData({
                userId: user.id,
              });
            }}
          />
        )}

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem
            onClick={() => {
              handleMenuClose();
              handelPauseReport(selectAccount!);
            }}
            sx={{ padding: '12px 16px' }}
          >
            <img
              src={selectAccount?.status === 1 ? deactivateIcon : activateIcon}
              alt="Pause Schedule"
              style={{ marginRight: 8 }}
            />
            <Typography variant="body2" fontWeight={600}>
              {selectAccount?.status === 1 ? 'Deactivate' : 'Activate'} Account
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleMenuClose();
              handleDeleteReport(selectAccount!);
            }}
            sx={{ padding: '12px 16px' }}
          >
            <img src={deleteIcon} alt="Delete Schedule" style={{ marginRight: 8 }} />
            <Typography variant="body2" fontWeight={600} color="error">
              Delete Account
            </Typography>
          </MenuItem>
        </Menu>

        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
            siblingCount={0}
            boundaryCount={0}
            renderItem={(item) => {
              if (item.type === 'page' && item.page !== null && item.page > currentPage) {
                return null;
              }
              return <PaginationItem {...item} />;
            }}
          />
        </Box>
      </div>
    </>
  );
};

export default UserAccount;
